import React, { useEffect, useState, useCallback } from "react";
import DynamicForm from "./DynamicForm";
import { getFormDefinition } from "../../common/utils/invoke";
import Stepper from "../../common/utils/Stepper";
import DocumentUpload from "../../common/components/DocumentUpload";
import Review from "../../common/components/Review";
import Modal from "../../common/components/Modal.jsx";
import { submitAiInnovation } from "../../common/utils/invoke";
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from "../../constants";
import BrandLoader from "../../common/components/BrandLoader.jsx";

const AiHubForm = () => {
    const [sections, setSections] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [documents, setDocuments] = useState([]);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [apiErrorMsg, setApiErrorMsg] = useState(
        ERROR_MESSAGES.SUBMISSION_FAILED,
    );
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchDefinition = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getFormDefinition();

            const fieldsets = response.fields.filter(
                (field) => field.type === "fieldset"
            );

            setSections(fieldsets);
        } catch (error) {
            console.log(error);
        }
        finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDefinition();
    }, [fetchDefinition]);

    const handleChange = (fieldReference, value) => {
        value = value.replace(/^\s+/, "");

        switch (fieldReference) {
            case "Description":
                value = value.replace(/\s+/g, " ");
                break;

            case "proposalTopic":
                value = value.replace(/[^A-Za-z0-9&.,'()\- ]/g, "");
                value = value.replace(/\s+/g, " ");
                value = value.replace(/^[^A-Za-z]+/, "");

                break;

            case "companyName":
                // Letters, numbers, &, ., ,, ', (), -
                value = value.replace(/[^A-Za-z0-9&.,'()\- ]/g, "");
                value = value.replace(/\s+/g, " ");
                value = value.replace(/^[^A-Za-z]+/, "");

                break;

            case "contactPerson":
                // Only letters
                value = value.replace(/[^A-Za-z ]/g, "");
                value = value.replace(/\s+/g, " ");
                break;

            case "designation":
                // Letters, numbers, &, ., ,, (), -
                value = value.replace(/[^A-Za-z0-9&.,()\- ]/g, "");
                value = value.replace(/\s+/g, " ");
                value = value.replace(/^[^A-Za-z]+/, "");

                break;

            case "city":
                // Only letters
                value = value.replace(/[^A-Za-z ]/g, "");
                value = value.replace(/\s+/g, " ");
                break;

            case "emailID":
                value = value.replace(/[^A-Za-z0-9._%+-@]/g, "");
                break;
            case "mobileNumber":
                // Allow only digits
                value = value.replace(/\D/g, "");

                if (value.length === 1 && /^[0-5]$/.test(value)) {
                    return;
                }
                value = value.slice(0, 10);
                break;

            case "proposalCategoryOther":
                value = value.replace(/[^A-Za-z0-9&.,'()\- ]/g, "");
                value = value.replace(/\s+/g, " ");
                break;

            default:
                break;
        }

        setFormData((prev) => {
            const updated = {
                ...prev,
                [fieldReference]: value,
            };
            return updated;
        });

        setErrors((prev) => ({
            ...prev,
            [fieldReference]: "",
        }));
    };

    const currentSection = sections[currentStep];
    const visibleFields = currentSection?.nestedFields?.filter((field) => !field.hideField) || [];

    const validateCurrentStep = () => {
        const currentSection = sections[currentStep];

        if (!currentSection) return true;

        const validationErrors = {};

        currentSection.nestedFields?.forEach((field) => {
            const value = formData[field.fieldReference];

            // Required validation
            if (
                field.required &&
                (!value || value.toString().trim() === "" || (field.type === "select" && value.toLowerCase().startsWith("select")))
            ) {
                validationErrors[field.fieldReference] =
                    field.requiredErrorMessage?.en_US ||
                    `${field.label?.en_US} is required`;

                return;
            }
            if (
                currentSection.fieldReference === "documentUpload" &&
                documents.length === 0
            ) {
                validationErrors.documents =
                    "Please upload at least one document.";
            }

            if (
                field.fieldReference === "mobileNumber" &&
                value &&
                !/^[6-9]\d{9}$/.test(value)
            ) {
                validationErrors.mobileNumber =
                    "Please enter a valid Mobile Number.";
            }

            // Regex Validation
            const regexPattern = field.validation?.parameter?.en_US 

            if (regexPattern && value) {
                try {
                    const regex = new RegExp(regexPattern);

                    if (!regex.test(value)) {
                        validationErrors[field.fieldReference] =
                            field.validation?.errorMessage?.en_US ||
                            `Please enter valid ${field.fieldReference}`;
                    }
                } catch (err) {
                    console.error(
                        `Invalid regex for ${field.fieldReference}`,
                        err
                    );
                }
            }
        });

        setErrors(validationErrors);

        return Object.keys(validationErrors).length === 0;
    };

    const nextStep = () => {
        const isValid = validateCurrentStep();

        if (!isValid) return;

        if (currentStep < sections.length - 1) {
            setCurrentStep((prev) => prev + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const resetForm = () => {
        setFormData({});
        setErrors({});
        setDocuments([]);
        setCurrentStep(0);
        setSubmitSuccess(false);
    };

    const handleSubmit = async () => {
        const isValid = validateCurrentStep();

        if (!isValid) return;

        const payload = {
            companyInformation: {
                companyName: formData.companyName,
                website: formData.website,
                contactPerson: formData.contactPerson,
                designation: formData.designation,
                emailID: formData.emailID,
                mobileNumber: formData.mobileNumber,
                city: formData.city,
            },
            companySummary: {
                yearsInBusiness: formData.yearsInBusiness,
                numberOfEmployees: formData.numberOfEmployees,
                relevantIndustryExperience: formData.relevantIndustryExperience,
            },
            proposalDetails: {
                proposalTopic: formData.proposalTopic,
                description: formData.Description,
                proposalCategory: formData.proposalCategory,
            },
        };

        const multipartFormData = new FormData();
        multipartFormData.append("payload", JSON.stringify(payload));
        documents.forEach((file) => {
            multipartFormData.append("documents", file);
        });

        setLoading(true);
        try {
            const response = await submitAiInnovation(multipartFormData);
            if (response.status === "success") {
                setSubmitSuccess(true);
                setShowSuccessModal(true);
                return;
            }
            setApiErrorMsg(
                response?.errorMessage ||
                response?.error ||
                ERROR_MESSAGES.SUBMISSION_FAILED,
            );
            setShowErrorModal(true);
        } catch (error) {
            setApiErrorMsg(
                error?.response?.data?.errorMessage ||
                error?.response?.data?.error ||
                ERROR_MESSAGES.SUBMISSION_FAILED,
            );
            setShowErrorModal(true);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="loader-overlay">
                <BrandLoader color="#8b1a1a" size={12} />
            </div>
        );
    }

    return (
        <div className="claim-intimation-wrapper">
            {/* Stepper */}
            <Stepper
                steps={sections}
                currentStep={submitSuccess ? sections.length : currentStep}
                showStepName={true}
            />
            <div className="claim-intimation">
                <div className="claim-intimation__card">
                    <div className="claim-intimation__form-content">
                        <div className="claim-intimation__form-fields">
                            {currentSection && (
                                <>
                                    <div className="claim-intimation__header">
                                        <h4>{currentSection.label?.en_US}</h4>
                                    </div>
                                    <div className="claim-intimation__form">
                                        <DynamicForm
                                            fields={visibleFields}
                                            formData={formData}
                                            handleChange={handleChange}
                                            errors={errors}
                                        />
                                    </div>
                                </>
                            )}
                            {currentSection?.fieldReference === "documentUpload" && (
                                <DocumentUpload
                                    documents={documents}
                                    setDocuments={setDocuments}
                                    setErrors={setErrors}
                                    errors={errors.documents}
                                />
                            )}
                            {currentSection?.fieldReference === "reviewAndSubmit" && (
                                <Review
                                    sections={sections}
                                    formData={formData}
                                    documents={documents}
                                />
                            )}
                        </div>
                    </div>
                    <div className="form-actions-wrapper ">
                        <div className="form-actions-group">
                            {currentStep !== 0 && (
                                <button
                                    className="form-button btn  secondary-button regular"
                                    onClick={prevStep}
                                    disabled={currentStep === 0}
                                >
                                    Previous
                                </button>
                            )}

                            {currentStep < sections.length - 1 ? (
                                <button
                                    onClick={nextStep}
                                    className="form-button btn  primary-button regular"
                                >
                                    Next
                                </button>
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    className="form-button btn  primary-button regular"
                                >
                                    Submit
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                isOpen={showSuccessModal}
                onClose={() => {
                    setShowSuccessModal(false);
                    resetForm();
                }}
                title="You’re all set!"
                type="success"
                btnTitle="Done"
            >
                <p>{SUCCESS_MESSAGES.FORM_SUBMITTED}</p>
            </Modal>
            <Modal
                isOpen={showErrorModal}
                onClose={() => {
                    setShowErrorModal(false);
                    setApiErrorMsg(ERROR_MESSAGES.SUBMISSION_FAILED);
                }}
                title="Something went wrong!"
                type="error"
                btnTitle="Try Again"
            >
                <p>{apiErrorMsg}</p>
            </Modal>
        </div>
    );
};

export default AiHubForm;
